import React, { useEffect, useState, useCallback } from 'react';
import DataTable from "react-data-table-component";
import DataTableExtensions from "react-data-table-component-extensions";
import "react-data-table-component-extensions/dist/index.css";
import '../../modalright.css';
import '../../popup.css';
import '../../pricing-strategies.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export default function ServicesList() {
    const API_URL = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();
    
    // State variables
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [serviceToDelete, setServiceToDelete] = useState(null);

    // Fetch all services on component mount
    useEffect(() => {
        fetchServices();
    }, [statusFilter]);

    // Fetch services from API
    const fetchServices = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}api/pricing/services`);
            
            if (response.data.success) {
                let servicesData = response.data.data || [];
                
                if (statusFilter === 'active') {
                    servicesData = servicesData.filter(service => service.isActive);
                } else if (statusFilter === 'inactive') {
                    servicesData = servicesData.filter(service => !service.isActive);
                }
                
                setServices(servicesData);
            } else {
                setError('Failed to fetch services');
            }
        } catch (err) {
            console.error('Error fetching services:', err);
            setError('Error fetching services. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Delete single service
    const handleDeleteService = async () => {
        if (!serviceToDelete) return;
        
        try {
            const response = await axios.delete(`${API_URL}api/pricing/services/${serviceToDelete.id}`);
            
            if (response.data.success) {
                setServices(services.filter(s => s.id !== serviceToDelete.id));
                alert('Service deleted successfully');
            } else {
                alert('Failed to delete service');
            }
        } catch (err) {
            console.error('Error deleting service:', err);
            alert('Error deleting service. Please try again.');
        } finally {
            setDeleteModalOpen(false);
            setServiceToDelete(null);
        }
    };

    // Handle row selection
    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    // Navigate to create service page
    const handleAddService = () => {
        navigate('/orders/services/create');
    };

    // Navigate to edit service page
    const handleEditService = (service) => {
        navigate(`/orders/services/edit/${service.id}`);
    };

    // Open delete confirmation modal
    const openDeleteModal = (service) => {
        setServiceToDelete(service);
        setDeleteModalOpen(true);
    };

    // DataTable columns
    const columns = [
        {
            name: 'Service Code',
            selector: row => row.serviceCode,
            sortable: true,
            cell: row => (
                <span className="zone-code-cell">{row.serviceCode}</span>
            ),
        },
        {
            name: 'Service Name',
            selector: row => row.serviceName,
            sortable: true,
        },
        {
            name: 'Service Type',
            selector: row => row.serviceType || '-',
            sortable: true,
        },
        {
            name: 'Description',
            selector: row => row.description || '-',
            sortable: true,
            cell: row => (
                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', textAlign: 'left', maxWidth: '200px' }}>
                    {row.description || '-'}
                </div>
            ),
        },
        {
            name: 'Status',
            selector: row => row.isActive,
            sortable: true,
            cell: row => (
                <span className={`status-badge ${row.isActive ? 'status-active' : 'status-inactive'}`}>
                    {row.isActive ? 'Active' : 'Inactive'}
                </span>
            ),
        },
        {
            name: 'Actions',
            cell: row => (
                <div className="action-buttons">
                    <button 
                        className="btn-action btn-edit"
                        onClick={() => handleEditService(row)}
                        title="Edit Service"
                    >
                        <i className="fa fa-pencil" aria-hidden="true"></i>
                    </button>
                    <button 
                        className="btn-action btn-delete"
                        onClick={() => openDeleteModal(row)}
                        title="Delete Service"
                    >
                        <i className="fa fa-trash" aria-hidden="true"></i>
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    // Custom styles for DataTable
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
            },
        },
        headCells: {
            style: {
                fontWeight: '600',
                fontSize: '14px',
                color: '#495057',
            },
        },
        rows: {
            style: {
                minHeight: '52px',
                '&:hover': {
                    backgroundColor: '#f1f3f4',
                },
            },
        },
    };

    const tableData = {
        columns,
        data: services,
    };

    return (
        <div className="zones-list-container">
            {/* Page Header */}
            <div className="page-header">
                <div className="header-left">
                    <h2>
                        <i className="fa fa-truck" aria-hidden="true"></i>
                        Services Management
                    </h2>
                    <p className="subtitle">Manage delivery service types and configurations</p>
                </div>
                <div className="header-right">
                    <button className="btn btn-primary btn-add-zone" onClick={handleAddService}>
                        <i className="fa fa-plus" aria-hidden="true"></i>
                        Add New Service
                    </button>
                </div>
            </div>

            {/* Filters and Search */}
            <div className="filters-section">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Search services..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button className="btn-search">
                        <i className="fa fa-search" aria-hidden="true"></i>
                    </button>
                </div>
                
                <div className="filter-dropdown">
                    <label>Status:</label>
                    <select 
                        value={statusFilter} 
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="alert alert-danger">
                    <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
                    {error}
                </div>
            )}

            {/* DataTable */}
            <div className="table-container">
                <DataTableExtensions {...tableData} print={false} export={true} filterHidden={true}>
                    <DataTable
                        columns={columns}
                        data={services}
                        pagination
                        paginationPerPage={10}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        selectableRows
                        selectableRowsHighlight
                        onSelectedRowsChange={handleRowSelected}
                        clearSelectedRows={toggleCleared}
                        progressPending={loading}
                        progressComponent={
                            <div className="loading-spinner">
                                <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                                Loading services...
                            </div>
                        }
                        noDataComponent={
                            <div className="no-data">
                                <i className="fa fa-truck" aria-hidden="true"></i>
                                <p>No services found</p>
                                <button className="btn btn-primary" onClick={handleAddService}>
                                    Create First Service
                                </button>
                            </div>
                        }
                        customStyles={customStyles}
                        highlightOnHover
                        pointerOnHover
                    />
                </DataTableExtensions>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content delete-modal">
                        <div className="modal-header">
                            <h3>Confirm Delete</h3>
                            <button className="close-btn" onClick={() => setDeleteModalOpen(false)}>
                                <i className="fa fa-times" aria-hidden="true"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete service <strong>"{serviceToDelete?.serviceName}"</strong>?</p>
                            <p className="warning-text">
                                <i className="fa fa-exclamation-triangle" aria-hidden="true"></i>
                                This action cannot be undone.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setDeleteModalOpen(false)}>
                                Cancel
                            </button>
                            <button className="btn btn-danger" onClick={handleDeleteService}>
                                Delete Service
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
